// Référentiel de présentation des KPI du tableau de bord (cockpit d'action).
// Source unique : libellé, picto, page de destination (navigation), nature du
// chiffre et seuils d'état. Le backend (GET /api/dashboard/kpis) ne renvoie que
// { key, value, extra } ; tout l'habillage est défini ici.
//
// kind :
//   'count' — compteur « reste à faire » : 0 = à jour, sinon surveiller, ≥ urgent = urgent
//   'rate'  — taux (%) où plus haut = mieux : ≥ ok = bon, ≥ warn = à surveiller, sinon urgent
//   'info'  — indicateur d'activité (travail en cours), neutre quelle que soit la valeur

export const KPI_META = {
  demandes_a_traiter: {
    icon: '💬', label: 'Demandes clients à traiter', nav: 'leads-services',
    kind: 'count', urgent: 60, hint: 'En attente de traitement', zero: 'Tout est traité',
  },
  leads_web_non_traites: {
    icon: '🌐', label: 'Leads web non traités', nav: 'leads',
    kind: 'count', urgent: 40, hint: 'Non archivés (affectés ou non)', zero: 'Tous traités',
  },
  ringover_taux_service: {
    icon: '☎️', label: 'Taux de service standard', nav: 'ringover-reporting',
    kind: 'rate', ok: 90, warn: 80, hint: "Appels entrants pris aujourd'hui",
  },
  litiges_en_cours: {
    icon: '⚖️', label: 'Litiges en cours', nav: 'litiges',
    kind: 'info', hint: 'Dossiers non clôturés',
  },
  litiges_en_retard: {
    icon: '⚖️', label: 'Prochaine action à traiter', nav: 'litiges',
    kind: 'count', urgent: 5, hint: 'Dépassée ou non renseignée', zero: 'Aucun retard',
  },
  reception_vip_a_traiter: {
    icon: '🏁', label: 'Réceptions VIP en cours', nav: 'reception-vip',
    kind: 'count', urgent: 10, hint: 'Véhicules non restitués', zero: 'Aucune en cours',
  },
  file_magasin_attente: {
    icon: '🛎️', label: 'File magasin en attente', nav: 'file-virtuelle-magasin',
    kind: 'count', urgent: 6, hint: 'Clients au comptoir Pièces', zero: 'File vide',
  },
  relances_atelier: {
    icon: '🔔', label: 'Relances atelier en retard', nav: 'relances-atelier',
    kind: 'count', urgent: 50, hint: 'Révisions à relancer', zero: 'À jour',
  },
  gmb_avis_a_traiter: {
    icon: '🌟', label: 'Google', nav: 'gmb-reviews',
    kind: 'count', urgent: 20, hint: 'Avis sans réponse (depuis 2026)', zero: 'Tous répondus',
  },
  garanties_a_soumettre_7j: {
    icon: '🛡️', label: 'Garanties à soumettre (> 7 j)', nav: 'garantie',
    kind: 'count', urgent: 10, hint: 'Facturées 2026, à soumettre depuis +7 j', zero: 'Aucune en retard',
  },
  jdma_avis_a_traiter: {
    icon: '⭐', label: 'JDMA', nav: 'jdma',
    kind: 'count', urgent: 20, hint: 'Avis VN + APV depuis le 01/07/2026 sans recontact', zero: 'Tous traités',
  },
  parking_par_emplacement: {
    icon: '🅿️', label: 'Parking', nav: 'parking',
    kind: 'info', hint: 'Véhicules par emplacement',
  },
};

// Répartition par service, affichée en blocs dédiés (5 cartes) au lieu d'une carte
// unique — pour demandes_a_traiter (1ʳᵉ ligne) et leads_web_non_traites (2ᵉ ligne).
// Mêmes libellés/pictos que SuiviCollaborateursView (frontend/src/components/
// LeadsServices.js, constantes SERVICE_LABELS/SERVICE_ICONS).
export const SERVICE_BREAKDOWN = [
  { key: 'VN', label: 'VN', icon: '🚘' },
  { key: 'VO', label: 'VO', icon: '🔄' },
  { key: 'ATELIER', label: 'Atelier', icon: '🔧' },
  { key: 'PIECES', label: 'Pièces', icon: '📦' },
  { key: 'AUTRE', label: 'Autres', icon: '📋' },
];

// KPI affichés en blocs par service plutôt qu'en carte agrégée unique (première
// et deuxième lignes du tableau de bord) — extraits de la grille générique par
// DashboardHome.js. Seuil « urgent » par bloc = 1/3 du seuil global de la carte
// (calibré pour un total toutes catégories confondues, trop élevé par bloc).
export const SERVICE_BREAKDOWN_KPIS = ['demandes_a_traiter', 'leads_web_non_traites'];
export function serviceBreakdownState(kpiKey, value) {
  const urgent = Math.max(1, Math.round((KPI_META[kpiKey]?.urgent || 30) / 3));
  return kpiState({ kind: 'count', urgent }, value);
}

// KPI ventilés par une liste dynamique (pas un catalogue fixe comme SERVICE_BREAKDOWN) —
// ex. répartition par marque ou par emplacement, dont les valeurs possibles dépendent
// des données (extra.par_marque, extra.par_emplacement...). Contrairement à
// SERVICE_BREAKDOWN_KPIS, le seuil « urgent » par carte n'est PAS divisé : le seuil
// global (ex. 10 garanties à soumettre) reste pertinent tel quel pour une seule marque.
// L'état reprend le `kind` réel du KPI (ex. 'info' pour un simple compteur d'inventaire
// comme le parking, neutre — pas de notion d'« urgent » à répartir).
export const DYNAMIC_BREAKDOWN_KPIS = {
  garanties_a_soumettre_7j: 'par_marque',
  parking_par_emplacement: 'par_emplacement',
};

// Blocs de ventilation dynamique affichant en plus le total en haut à droite de
// l'en-tête (ex. Parking : utile de voir le volume global du parking d'un coup
// d'œil, en plus de la répartition par emplacement).
export const DYNAMIC_BREAKDOWN_SHOW_TOTAL = ['parking_par_emplacement'];
export function dynamicBreakdownState(kpiKey, value) {
  const meta = KPI_META[kpiKey] || {};
  return kpiState({ kind: meta.kind, urgent: meta.urgent, ok: meta.ok, warn: meta.warn }, value);
}

// Regroupements visuels de plusieurs KPI sous un même bloc titré — distinct de
// SERVICE_BREAKDOWN_KPIS : ici chaque carte reste un KPI indépendant avec son
// propre libellé/nav/seuils (pas une ventilation par service d'un seul KPI).
export const KPI_GROUPS = [
  {
    key: 'avis_clients',
    icon: '🌟',
    label: 'Avis clients à traiter',
    members: ['gmb_avis_a_traiter', 'jdma_avis_a_traiter'],
  },
  {
    key: 'litiges',
    icon: '⚖️',
    label: 'Litiges',
    members: ['litiges_en_cours', 'litiges_en_retard'],
  },
];

// Ordre d'affichage (les KPI absents de la réponse backend sont simplement ignorés).
export const KPI_ORDER = [
  'demandes_a_traiter',
  'leads_web_non_traites',
  'ringover_taux_service',
  'file_magasin_attente',
  'litiges_en_cours',
  'litiges_en_retard',
  'reception_vip_a_traiter',
  'relances_atelier',
  'gmb_avis_a_traiter',
  'garanties_a_soumettre_7j',
  'jdma_avis_a_traiter',
  'parking_par_emplacement',
];

// État visuel d'un KPI : 'ok' | 'warn' | 'urgent' | 'info' | 'empty'
export function kpiState(meta, value) {
  if (value == null) return meta.kind === 'rate' ? 'empty' : 'info';
  if (meta.kind === 'info') return 'info';
  if (meta.kind === 'rate') {
    if (value >= (meta.ok ?? 90)) return 'ok';
    if (value >= (meta.warn ?? 80)) return 'warn';
    return 'urgent';
  }
  // count : plus c'est bas, mieux c'est
  if (value <= 0) return 'ok';
  if (meta.urgent != null && value >= meta.urgent) return 'urgent';
  return 'warn';
}

// Valeur principale formatée pour l'affichage.
export function kpiDisplayValue(meta, value) {
  if (value == null) return '—';
  if (meta.kind === 'rate') return `${Number(value).toLocaleString('fr-FR')} %`;
  return Number(value).toLocaleString('fr-FR');
}
